import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Calendar, Image, X, ShoppingCart, Users, Instagram, Youtube } from 'lucide-react';
import './Home.css';
import albumElephant from '../assets/images/album-elephant.svg';
import cover1 from '../assets/images/cover-1.jpeg';
import usePageTitle from '../hooks/usePageTitle';
import NewsletterSignup from '../components/NewsletterSignup';
import LatestNews from '../components/LatestNews';

const Home: React.FC = () => {
  usePageTitle('Home - The Kults Official Site');
  const navigate = useNavigate();
  const [modal, setModal] = useState<null | { type: 'video'; title: string; description: string }>(null);

  const handleNavigation = (path: string) => {
    navigate(path);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const featuredVideos = [
    {
      id: 1,
      title: 'Typewriter Boxer',
      year: '2026',
      image: albumElephant,
      description: 'Official music video'
    },
    {
      id: 2,
      title: 'Nick of Time',
      year: '2026',
      image: albumElephant,
      description: 'Official music video'
    },
    {
      id: 3,
      title: 'Duality',
      year: '2026',
      image: albumElephant,
      description: 'Official music video'
    }
  ];



  const heroSocialLinks = [
    {
      icon: Instagram,
      url: 'https://www.instagram.com/the_kults_official/',
      label: 'Instagram',
    },
    {
      icon: Youtube,
      url: 'https://www.youtube.com/@thekultsofficial',
      label: 'YouTube',
    },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-social-mobile" aria-label="Social media">
          {heroSocialLinks.map((social) => (
            <a
              key={social.label}
              href={social.url}
              className="hero-social-link"
              aria-label={social.label}
              target="_blank"
              rel="noopener noreferrer"
            >
              <social.icon size={18} />
            </a>
          ))}
        </div>
        <div className="hero-content">
          <h1 className="hero-title">The Kults</h1>
          <p className="hero-subtitle">Basel's Finest</p>
          <div className="hero-buttons">
            <button
              type="button"
              className="hero-btn hero-btn-primary"
              onClick={() => handleNavigation('/music')}
            >
              <Play size={18} strokeWidth={2.5} aria-hidden="true" />
              Listen Now
            </button>
            <button
              type="button"
              className="hero-btn hero-btn-secondary"
              onClick={() => handleNavigation('/about')}
            >
              <Users size={18} strokeWidth={2.5} aria-hidden="true" />
              The Band
            </button>
          </div>
        </div>
        <div className="hero-background" style={{ backgroundImage: `url(${cover1})` }}></div>
      </section>

      {/* Featured Videos */}
      <section className="featured-albums">
        <div className="container">
          <h2 className="section-title">Featured Videos</h2>
          <div className="albums-grid">
            {featuredVideos.map((video) => (
              <div 
                key={video.id} 
                className="album-card"
                onClick={() => setModal({ type: 'video', title: video.title, description: video.description })}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setModal({ type: 'video', title: video.title, description: video.description });
                  }
                }}
              >
                <div className="album-image">
                  <img src={video.image} alt={video.title} />
                  <div className="album-overlay">
                    <Play size={40} />
                  </div>
                </div>
                <div className="album-info">
                  <h3>{video.title}</h3>
                  <p className="album-year">{video.year}</p>
                  <p className="album-description">{video.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="newsletter-section">
        <div className="container">
          <NewsletterSignup 
            title="Join The Kults Community"
            description="Get exclusive updates, early access to tickets, and behind-the-scenes content delivered to your inbox."
          />
        </div>
      </section>

      {/* Latest News */}
      <LatestNews />

      {/* Call to Action */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Explore More</h2>
            <p>Discover our music, merchandise, and latest updates.</p>
            <div className="cta-buttons">
              <button 
                className="btn btn-primary" 
                onClick={() => handleNavigation('/store')}
              >
                <ShoppingCart size={20} />
                Visit Store
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => handleNavigation('/gallery')}
              >
                <Image size={20} />
                View Gallery
              </button>
              <button 
                className="btn btn-tertiary" 
                onClick={() => handleNavigation('/tour')}
              >
                <Calendar size={20} />
                Upcoming Shows
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {modal && (
        <div className="gallery-modal" onClick={() => setModal(null)}>
          <button className="close-btn" onClick={e => { e.stopPropagation(); setModal(null); }} aria-label="Close modal">
            <X />
          </button>
          <div className="gallery-modal-content" onClick={e => e.stopPropagation()}>
            <div className="video-placeholder">
              <Play size={64} style={{ marginRight: 16 }} />
              Video player coming soon
            </div>
            <div className="modal-title">{modal.title}</div>
            <div className="modal-desc">{modal.description}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home; 